import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Building, Phone, Mail, MapPin, Globe, Image, Upload, Trash2, Eye, EyeOff, Plus } from 'lucide-react'
import { supabase, uploadFile, getPublicUrl } from '@/lib/supabase'
import { RichTextEditor } from '@/components/admin/RichTextEditor'

interface Settings {
  contact_info: {
    company_name: string
    address: string
    city: string
    country: string
    phone: string
    email: string
    footer_logo: string
    footer_logo_link: string
  }
  social_links: {
    linkedin: string
    facebook: string
  }
  about_content: {
    hero_title: string
    hero_description: string
    section_title: string
    section_content: string
    image_url: string
    stats: { value: string; label: string }[]
  }
  hero_settings: {
    background_image: string
    background_opacity: number
    logo_image: string
    logo_link: string
    title: string
    title_highlight: string
    description: string
    button1_text: string
    button2_text: string
  }
  visibility_settings: {
    show_about: boolean
    show_products: boolean
    show_gallery: boolean
    show_certificates: boolean
    show_contact: boolean
  }
}

const defaultSettings: Settings = {
  contact_info: {
    company_name: 'EngiMarineX',
    address: 'Tuzla Shipyard Area',
    city: 'Istanbul',
    country: 'Turkey',
    phone: '+90 216 555 1234',
    email: 'info@engimarinex.com',
    footer_logo: '',
    footer_logo_link: '',
  },
  social_links: {
    linkedin: '',
    facebook: '',
  },
  about_content: {
    hero_title: 'About EngiMarineX',
    hero_description: 'A leading provider of marine equipment solutions, delivering high-quality products to the maritime industry worldwide.',
    section_title: 'Your Trusted Partner in Marine Equipment',
    section_content: '<p>EngiMarineX specializes in the design, manufacturing, and supply of premium marine equipment. Our comprehensive product range includes watertight doors, hatches, PV valves, ventilation systems, and deck equipment.</p><p>With decades of combined experience in the maritime industry, our team understands the unique challenges faced by shipbuilders, operators, and repair yards. We are committed to delivering products that meet the highest standards of quality and safety.</p><p>All our products are certified by leading classification societies including DNV/GL, Bureau Veritas, Lloyd\'s Register, RINA, and ABS, ensuring compliance with international maritime regulations.</p>',
    image_url: '',
    stats: [
      { value: '20+', label: 'Years Experience' },
      { value: '500+', label: 'Projects Completed' },
      { value: '50+', label: 'Countries Served' },
      { value: '100%', label: 'Certified Products' },
    ],
  },
  hero_settings: {
    background_image: '',
    background_opacity: 30,
    logo_image: '',
    logo_link: '',
    title: 'Marine Equipment',
    title_highlight: 'Solutions',
    description: 'High-quality doors, hatches, valves, and deck equipment for the maritime industry. Certified by leading classification societies.',
    button1_text: 'Explore Products',
    button2_text: 'Contact Us',
  },
  visibility_settings: {
    show_about: true,
    show_products: true,
    show_gallery: true,
    show_certificates: true,
    show_contact: true,
  },
}

export function SettingsPage() {
  const [settings, setSettings] = useState<Settings>(defaultSettings)
  const [activeTab, setActiveTab] = useState<'hero' | 'contact' | 'social' | 'about' | 'visibility'>('hero')
  const [isUploading, setIsUploading] = useState(false)
  const queryClient = useQueryClient()

  const { data: savedSettings, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('site_settings').select('*')
      if (error) throw error

      const result: Partial<Settings> = {}
      data.forEach((row) => {
        result[row.key as keyof Settings] = row.value
      })
      return result as Settings
    },
  })

  useEffect(() => {
    if (savedSettings) {
      setSettings({
        contact_info: savedSettings.contact_info || defaultSettings.contact_info,
        social_links: savedSettings.social_links || defaultSettings.social_links,
        about_content: {
          ...defaultSettings.about_content,
          ...savedSettings.about_content,
          stats: savedSettings.about_content?.stats || defaultSettings.about_content.stats,
        },
        hero_settings: savedSettings.hero_settings || defaultSettings.hero_settings,
        visibility_settings: savedSettings.visibility_settings || defaultSettings.visibility_settings,
      })
    }
  }, [savedSettings])

  const saveMutation = useMutation({
    mutationFn: async (newSettings: Settings) => {
      for (const [key, value] of Object.entries(newSettings)) {
        await supabase.from('site_settings').upsert({ key, value }, { onConflict: 'key' })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] })
      queryClient.invalidateQueries({ queryKey: ['contact-info'] })
      queryClient.invalidateQueries({ queryKey: ['maintenance-mode'] })
      queryClient.invalidateQueries({ queryKey: ['hero-settings'] })
      queryClient.invalidateQueries({ queryKey: ['visibility-settings'] })
    },
  })

  const handleSave = () => {
    saveMutation.mutate(settings)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Settings</h1>
          <p className="text-secondary-600">Manage site settings and content</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="btn-primary"
        >
          <Save className="w-5 h-5 mr-2" />
          {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-secondary-200">
        {[
          { id: 'hero', label: 'Hero Section', icon: Image },
          { id: 'contact', label: 'Contact Info', icon: Building },
          { id: 'social', label: 'Social Links', icon: Globe },
          { id: 'about', label: 'About Content', icon: MapPin },
          { id: 'visibility', label: 'Page Visibility', icon: Eye },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card p-6">
        {activeTab === 'hero' && (
          <div className="space-y-6 max-w-xl">
            {/* Background Image Upload */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                <Image className="w-4 h-4 inline mr-1" /> Background Image
              </label>

              {settings.hero_settings.background_image ? (
                <div className="relative">
                  <div
                    className="relative w-full h-48 rounded-lg overflow-hidden bg-secondary-900"
                  >
                    <img
                      src={settings.hero_settings.background_image}
                      alt="Hero background"
                      className="w-full h-full object-cover"
                      style={{ opacity: settings.hero_settings.background_opacity / 100 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary-900/50 via-secondary-800/30 to-primary-900/50" />
                  </div>
                  <button
                    onClick={() => setSettings({
                      ...settings,
                      hero_settings: { ...settings.hero_settings, background_image: '' }
                    })}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-secondary-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-2" />
                      <span className="text-sm text-secondary-500">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-secondary-400 mb-2" />
                      <span className="text-sm text-secondary-600">Click to upload background image</span>
                      <span className="text-xs text-secondary-400 mt-1">Recommended: 1920x1080 or larger</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={isUploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        setIsUploading(true)
                        try {
                          const path = `hero/${Date.now()}-${file.name}`
                          await uploadFile('gallery', path, file)
                          const url = getPublicUrl('gallery', path)
                          setSettings({
                            ...settings,
                            hero_settings: { ...settings.hero_settings, background_image: url }
                          })
                        } catch (err) {
                          console.error('Upload failed:', err)
                        } finally {
                          setIsUploading(false)
                        }
                      }
                    }}
                  />
                </label>
              )}
            </div>

            {/* Opacity Slider */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Background Opacity: {settings.hero_settings.background_opacity}%
              </label>
              <input
                type="range"
                min="10"
                max="100"
                value={settings.hero_settings.background_opacity}
                onChange={(e) => setSettings({
                  ...settings,
                  hero_settings: { ...settings.hero_settings, background_opacity: parseInt(e.target.value) }
                })}
                className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              />
              <div className="flex justify-between text-xs text-secondary-400 mt-1">
                <span>10% (More transparent)</span>
                <span>100% (Fully visible)</span>
              </div>
            </div>

            <div className="border-t border-secondary-200 pt-6 mt-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Hero Content</h3>

              <div className="space-y-4">
                {/* Hero Logo */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Hero Logo/Badge
                  </label>
                  {settings.hero_settings.logo_image ? (
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <img
                          src={settings.hero_settings.logo_image}
                          alt="Hero logo"
                          className="h-16 w-auto object-contain bg-secondary-100 rounded-lg p-2"
                        />
                        <button
                          type="button"
                          onClick={() => setSettings({
                            ...settings,
                            hero_settings: { ...settings.hero_settings, logo_image: '' }
                          })}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-sm text-green-600">Logo uploaded</span>
                    </div>
                  ) : (
                    <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-secondary-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors w-fit">
                      <Upload className="w-5 h-5 text-secondary-400" />
                      <span className="text-sm text-secondary-600">Upload Logo Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            try {
                              const path = `hero/logo-${Date.now()}-${file.name}`
                              await uploadFile('gallery', path, file)
                              const url = getPublicUrl('gallery', path)
                              setSettings({
                                ...settings,
                                hero_settings: { ...settings.hero_settings, logo_image: url }
                              })
                            } catch (err) {
                              console.error('Upload failed:', err)
                            }
                          }
                        }}
                      />
                    </label>
                  )}
                </div>

                {/* Logo Link */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Logo Link (opens in new tab)
                  </label>
                  <input
                    type="url"
                    value={settings.hero_settings.logo_link}
                    onChange={(e) => setSettings({
                      ...settings,
                      hero_settings: { ...settings.hero_settings, logo_link: e.target.value }
                    })}
                    className="input"
                    placeholder="https://example.com"
                  />
                  <p className="text-xs text-secondary-400 mt-1">Leave empty for no link</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Title (Main)
                    </label>
                    <input
                      type="text"
                      value={settings.hero_settings.title}
                      onChange={(e) => setSettings({
                        ...settings,
                        hero_settings: { ...settings.hero_settings, title: e.target.value }
                      })}
                      className="input"
                      placeholder="Marine Equipment"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Title (Highlighted)
                    </label>
                    <input
                      type="text"
                      value={settings.hero_settings.title_highlight}
                      onChange={(e) => setSettings({
                        ...settings,
                        hero_settings: { ...settings.hero_settings, title_highlight: e.target.value }
                      })}
                      className="input"
                      placeholder="Solutions"
                    />
                    <p className="text-xs text-secondary-400 mt-1">This part will be shown in accent color</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={settings.hero_settings.description}
                    onChange={(e) => setSettings({
                      ...settings,
                      hero_settings: { ...settings.hero_settings, description: e.target.value }
                    })}
                    rows={3}
                    className="input resize-none"
                    placeholder="High-quality doors, hatches..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Primary Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.hero_settings.button1_text}
                      onChange={(e) => setSettings({
                        ...settings,
                        hero_settings: { ...settings.hero_settings, button1_text: e.target.value }
                      })}
                      className="input"
                      placeholder="Explore Products"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Secondary Button Text
                    </label>
                    <input
                      type="text"
                      value={settings.hero_settings.button2_text}
                      onChange={(e) => setSettings({
                        ...settings,
                        hero_settings: { ...settings.hero_settings, button2_text: e.target.value }
                      })}
                      className="input"
                      placeholder="Contact Us"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                <Building className="w-4 h-4 inline mr-1" /> Company Name
              </label>
              <input
                type="text"
                value={settings.contact_info.company_name}
                onChange={(e) => setSettings({
                  ...settings,
                  contact_info: { ...settings.contact_info, company_name: e.target.value }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                <MapPin className="w-4 h-4 inline mr-1" /> Address
              </label>
              <input
                type="text"
                value={settings.contact_info.address}
                onChange={(e) => setSettings({
                  ...settings,
                  contact_info: { ...settings.contact_info, address: e.target.value }
                })}
                className="input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">City</label>
                <input
                  type="text"
                  value={settings.contact_info.city}
                  onChange={(e) => setSettings({
                    ...settings,
                    contact_info: { ...settings.contact_info, city: e.target.value }
                  })}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Country</label>
                <input
                  type="text"
                  value={settings.contact_info.country}
                  onChange={(e) => setSettings({
                    ...settings,
                    contact_info: { ...settings.contact_info, country: e.target.value }
                  })}
                  className="input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                <Phone className="w-4 h-4 inline mr-1" /> Phone
              </label>
              <input
                type="text"
                value={settings.contact_info.phone}
                onChange={(e) => setSettings({
                  ...settings,
                  contact_info: { ...settings.contact_info, phone: e.target.value }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                <Mail className="w-4 h-4 inline mr-1" /> Email
              </label>
              <input
                type="email"
                value={settings.contact_info.email}
                onChange={(e) => setSettings({
                  ...settings,
                  contact_info: { ...settings.contact_info, email: e.target.value }
                })}
                className="input"
              />
            </div>

            {/* Footer Contact Logo */}
            <div className="border-t border-secondary-200 pt-4 mt-4">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                <Image className="w-4 h-4 inline mr-1" /> Footer Contact Logo
              </label>
              <p className="text-xs text-secondary-500 mb-3">
                This logo will appear below the contact info in the footer
              </p>
              {settings.contact_info.footer_logo ? (
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={settings.contact_info.footer_logo}
                      alt="Footer logo"
                      className="h-16 w-auto object-contain bg-secondary-800 rounded-lg p-2"
                    />
                    <button
                      type="button"
                      onClick={() => setSettings({
                        ...settings,
                        contact_info: { ...settings.contact_info, footer_logo: '' }
                      })}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <span className="text-sm text-green-600">Logo uploaded</span>
                </div>
              ) : (
                <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-secondary-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors w-fit">
                  <Upload className="w-5 h-5 text-secondary-400" />
                  <span className="text-sm text-secondary-600">Upload Footer Logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        try {
                          const path = `footer/logo-${Date.now()}-${file.name}`
                          await uploadFile('gallery', path, file)
                          const url = getPublicUrl('gallery', path)
                          setSettings({
                            ...settings,
                            contact_info: { ...settings.contact_info, footer_logo: url }
                          })
                        } catch (err) {
                          console.error('Upload failed:', err)
                        }
                      }
                    }}
                  />
                </label>
              )}

              {/* Footer Logo Link */}
              <div className="mt-3">
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Footer Logo Link (opens in new tab)
                </label>
                <input
                  type="url"
                  value={settings.contact_info.footer_logo_link || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    contact_info: { ...settings.contact_info, footer_logo_link: e.target.value }
                  })}
                  className="input"
                  placeholder="https://example.com"
                />
                <p className="text-xs text-secondary-400 mt-1">Leave empty for no link</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'social' && (
          <div className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">LinkedIn URL</label>
              <input
                type="url"
                value={settings.social_links.linkedin}
                onChange={(e) => setSettings({
                  ...settings,
                  social_links: { ...settings.social_links, linkedin: e.target.value }
                })}
                className="input"
                placeholder="https://linkedin.com/company/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">Facebook URL</label>
              <input
                type="url"
                value={settings.social_links.facebook}
                onChange={(e) => setSettings({
                  ...settings,
                  social_links: { ...settings.social_links, facebook: e.target.value }
                })}
                className="input"
                placeholder="https://facebook.com/..."
              />
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="space-y-6 max-w-2xl">
            {/* Hero Section */}
            <div className="border-b border-secondary-200 pb-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Hero Section</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Hero Title</label>
                  <input
                    type="text"
                    value={settings.about_content.hero_title}
                    onChange={(e) => setSettings({
                      ...settings,
                      about_content: { ...settings.about_content, hero_title: e.target.value }
                    })}
                    className="input"
                    placeholder="About EngiMarineX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Hero Description</label>
                  <textarea
                    value={settings.about_content.hero_description}
                    onChange={(e) => setSettings({
                      ...settings,
                      about_content: { ...settings.about_content, hero_description: e.target.value }
                    })}
                    rows={3}
                    className="input resize-none"
                    placeholder="A leading provider of marine equipment..."
                  />
                </div>
              </div>
            </div>

            {/* Main Content Section */}
            <div className="border-b border-secondary-200 pb-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Main Content</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Section Title</label>
                  <input
                    type="text"
                    value={settings.about_content.section_title}
                    onChange={(e) => setSettings({
                      ...settings,
                      about_content: { ...settings.about_content, section_title: e.target.value }
                    })}
                    className="input"
                    placeholder="Your Trusted Partner in Marine Equipment"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Content</label>
                  <RichTextEditor
                    content={settings.about_content.section_content}
                    onChange={(html) => setSettings({
                      ...settings,
                      about_content: { ...settings.about_content, section_content: html }
                    })}
                    placeholder="Write about your company..."
                  />
                </div>
              </div>
            </div>

            {/* Company Image */}
            <div className="border-b border-secondary-200 pb-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Company Image</h3>
              {settings.about_content.image_url ? (
                <div className="relative inline-block">
                  <img
                    src={settings.about_content.image_url}
                    alt="Company"
                    className="w-64 h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setSettings({
                      ...settings,
                      about_content: { ...settings.about_content, image_url: '' }
                    })}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-64 h-48 border-2 border-dashed border-secondary-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
                  <Upload className="w-8 h-8 text-secondary-400 mb-2" />
                  <span className="text-sm text-secondary-600">Upload Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        try {
                          const path = `about/${Date.now()}-${file.name}`
                          await uploadFile('gallery', path, file)
                          const url = getPublicUrl('gallery', path)
                          setSettings({
                            ...settings,
                            about_content: { ...settings.about_content, image_url: url }
                          })
                        } catch (err) {
                          console.error('Upload failed:', err)
                        }
                      }
                    }}
                  />
                </label>
              )}
            </div>

            {/* Statistics */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-secondary-900">Statistics</h3>
                <button
                  type="button"
                  onClick={() => setSettings({
                    ...settings,
                    about_content: {
                      ...settings.about_content,
                      stats: [...(settings.about_content.stats || []), { value: '', label: '' }]
                    }
                  })}
                  className="btn-outline btn-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Stat
                </button>
              </div>
              <div className="space-y-3">
                {(settings.about_content.stats || []).map((stat, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <input
                      type="text"
                      value={stat.value}
                      onChange={(e) => {
                        const newStats = [...(settings.about_content.stats || [])]
                        newStats[index].value = e.target.value
                        setSettings({
                          ...settings,
                          about_content: { ...settings.about_content, stats: newStats }
                        })
                      }}
                      className="input w-24"
                      placeholder="20+"
                    />
                    <input
                      type="text"
                      value={stat.label}
                      onChange={(e) => {
                        const newStats = [...(settings.about_content.stats || [])]
                        newStats[index].label = e.target.value
                        setSettings({
                          ...settings,
                          about_content: { ...settings.about_content, stats: newStats }
                        })
                      }}
                      className="input flex-1"
                      placeholder="Years Experience"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newStats = (settings.about_content.stats || []).filter((_, i) => i !== index)
                        setSettings({
                          ...settings,
                          about_content: { ...settings.about_content, stats: newStats }
                        })
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'visibility' && (
          <div className="space-y-6 max-w-xl">
            <p className="text-sm text-secondary-600">
              Control which pages are visible in the navigation menu. Home page is always visible.
            </p>

            <div className="space-y-3">
              {[
                { key: 'show_about', label: 'About', description: 'Company information page' },
                { key: 'show_products', label: 'Products & Services', description: 'Product catalog and categories' },
                { key: 'show_gallery', label: 'Gallery', description: 'Image and video gallery' },
                { key: 'show_certificates', label: 'Certificates', description: 'Certifications and accreditations' },
                { key: 'show_contact', label: 'Contact', description: 'Contact form and information' },
              ].map((item) => (
                <div
                  key={item.key}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                    settings.visibility_settings[item.key as keyof typeof settings.visibility_settings]
                      ? 'border-green-200 bg-green-50'
                      : 'border-secondary-200 bg-secondary-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {settings.visibility_settings[item.key as keyof typeof settings.visibility_settings] ? (
                      <Eye className="w-5 h-5 text-green-600" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-secondary-400" />
                    )}
                    <div>
                      <p className="font-medium text-secondary-900">{item.label}</p>
                      <p className="text-sm text-secondary-500">{item.description}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSettings({
                      ...settings,
                      visibility_settings: {
                        ...settings.visibility_settings,
                        [item.key]: !settings.visibility_settings[item.key as keyof typeof settings.visibility_settings]
                      }
                    })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.visibility_settings[item.key as keyof typeof settings.visibility_settings]
                        ? 'bg-green-600'
                        : 'bg-secondary-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.visibility_settings[item.key as keyof typeof settings.visibility_settings]
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Hidden pages will still be accessible via direct URL.
                This setting only controls the navigation menu visibility.
              </p>
            </div>
          </div>
        )}
      </div>

      {saveMutation.isSuccess && (
        <div className="p-4 bg-green-50 text-green-700 rounded-lg">
          Settings saved successfully!
        </div>
      )}
    </div>
  )
}
